// Campaign token validation service
// Reads ?t= from URL, validates via /v1/campaign-tokens/:token, shows banner
// After validation, writes token UTM fields into sessionData so they flow
// through to alpha_leads on contact form submission.
import { analytics } from '../utils/analytics';
import { updateSessionData, getSessionData } from '../utils/cookies';

export type CampaignPricingType = 'free' | 'discount_percent' | 'fixed_price' | 'override';

export interface CampaignPricing {
    type: CampaignPricingType;
    description?: string;
    parts_included: boolean;
    discount_percent?: number | null;
    fixed_price?: number | null;
    max_labour_minutes?: number | null;
    callout_fee?: number | null;
    callout_includes_minutes?: number | null;
    labour_rate_per_30min?: number | null;
}

/** Default company rates */
const DEFAULTS = { callout: 275, includesMinutes: 30, labourPer30: 66 } as const;

/** Resolve any pricing type into display-ready values */
export function resolvePricing(p: CampaignPricing | null | undefined) {
    if (!p) return null;
    const d = DEFAULTS;
    switch (p.type) {
        case 'free':
            return { callout: 0, includesMin: d.includesMinutes, labourPer30: 0, partsIncluded: p.parts_included, description: p.description, summary: 'FREE service callout' };
        case 'discount_percent': {
            const pct = p.discount_percent ?? 0;
            const callout = Math.round(d.callout * (1 - pct / 100));
            const labour = Math.round(d.labourPer30 * (1 - pct / 100));
            return { callout, includesMin: d.includesMinutes, labourPer30: labour, partsIncluded: p.parts_included, description: p.description, summary: `${pct}% off` };
        }
        case 'fixed_price':
            return { callout: p.fixed_price ?? 0, includesMin: p.max_labour_minutes ?? d.includesMinutes, labourPer30: 0, partsIncluded: p.parts_included, description: p.description, summary: `Flat fee $${p.fixed_price ?? 0}` };
        case 'override':
            return {
                callout: p.callout_fee ?? d.callout,
                includesMin: p.callout_includes_minutes ?? d.includesMinutes,
                labourPer30: p.labour_rate_per_30min ?? d.labourPer30,
                partsIncluded: p.parts_included,
                description: p.description,
                summary: 'Custom pricing',
            };
        default:
            return null;
    }
}

export interface CampaignTokenData {
    valid: boolean;
    redeemed: boolean;
    company_id: number;
    contact_id: number;
    banner: {
        title: string;
        message: string;
        style: string;
    };
    pricing?: CampaignPricing;
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_content?: string;
    utm_term?: string;
    /** Known contact details from our records — seeded into session so ContactForm
     *  prefills instead of the customer retyping. Address is deliberately excluded
     *  (could be stale by the time an old customer re-books); left for the customer
     *  to confirm/enter. */
    prefill?: {
        first_name?: string;
        last_name?: string;
        email?: string;
        phone?: string;
    };
}

const CAMPAIGN_TOKEN_BASE = '/v1/campaign-tokens';

class CampaignTokenService {
    private static instance: CampaignTokenService;
    private tokenData: CampaignTokenData | null = null;
    private leadId: string | null = null;
    private token: string | null = null;
    /** Resolves once URL-token validation (and any session prefill write) has
     *  finished — the fetch is async, so consumers that need prefill data
     *  (e.g. ContactForm re-checking session after its own sync mount-effect
     *  ran) should await this instead of assuming checkUrlForToken is done. */
    public readonly ready: Promise<void>;

    constructor() {
        this.ready = this.checkUrlForToken();
    }

    static getInstance(): CampaignTokenService {
        if (!CampaignTokenService.instance) {
            CampaignTokenService.instance = new CampaignTokenService();
        }
        return CampaignTokenService.instance;
    }

    private async checkUrlForToken(): Promise<void> {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('t');
        this.leadId = params.get('lead_id');

        if (!token) return;
        this.token = token;

        try {
            const response = await fetch(`${CAMPAIGN_TOKEN_BASE}/${encodeURIComponent(token)}`);

            if (response.ok) {
                const data: CampaignTokenData = await response.json();
                this.tokenData = data;
                this.showBanner(data.banner);

                // Write token UTM fields into session so they reach alpha_leads on form submit
                if (data.utm_source || data.utm_medium || data.utm_campaign) {
                    updateSessionData({
                        utmData: {
                            source: data.utm_source,
                            medium: data.utm_medium,
                            campaign: data.utm_campaign,
                            content: data.utm_content,
                            term: data.utm_term,
                        }
                    });
                    console.log('✅ Campaign token UTM written to session:', {
                        source: data.utm_source,
                        medium: data.utm_medium,
                        campaign: data.utm_campaign,
                    });
                }

                // Prefill known contact details (name/email/phone) so the customer doesn't
                // retype what we already know — address stays blank for them to confirm since
                // it can change over the years. Seeded into the SAME session keys ContactForm
                // already restores from on mount (formData), so no form changes are needed.
                // Never overwrites an in-progress session's own edits.
                if (data.prefill && (data.prefill.first_name || data.prefill.email || data.prefill.phone)) {
                    const existing = getSessionData();
                    if (!existing?.formData?.firstName && !existing?.formData?.email) {
                        updateSessionData({
                            formData: {
                                ...(existing?.formData || {}),
                                firstName: data.prefill.first_name || '',
                                lastName: data.prefill.last_name || '',
                                email: data.prefill.email || '',
                                phone: data.prefill.phone || '',
                            },
                        });
                    }
                    // Default service type to Service Request (the common campaign case) —
                    // the customer can still change it; also read by DetailsForm on mount.
                    if (!existing?.bookingData?.serviceType) {
                        updateSessionData({
                            bookingData: { ...(existing?.bookingData || {}), serviceType: 'service-request' },
                        });
                    }
                    console.log('✅ Campaign prefill seeded into session:', data.prefill);
                }

                analytics.track('campaign_token_validated', 'campaign_token_service', {
                    contact_id: data.contact_id,
                    utm_source: data.utm_source,
                    utm_campaign: data.utm_campaign,
                    lead_id: this.leadId,
                });
            } else {
                const errorData = await response.json().catch(() => ({}));
                console.warn(`⚠️ Campaign token invalid (${response.status}):`, errorData.error);

                analytics.track('campaign_token_failed', 'campaign_token_service', {
                    status: response.status,
                    error: errorData.error,
                });
            }
        } catch (error) {
            console.error('Failed to validate campaign token:', error);
        }
    }

    private showBanner(banner: CampaignTokenData['banner']): void {
        if (!banner?.title && !banner?.message) return;

        const styleMap: Record<string, { bg: string; icon: string }> = {
            info: { bg: '#3B82F6', icon: 'ℹ️' },
            success: { bg: '#10B981', icon: '✅' },
            welcome: { bg: '#8B5CF6', icon: '👋' },
            promo: { bg: '#F59E0B', icon: '🎉' },
        };
        const { bg, icon } = styleMap[banner.style] || styleMap.info;

        const el = document.createElement('div');
        el.id = 'campaign-token-banner';
        el.style.cssText = `
            position: fixed;
            top: 0; left: 0; right: 0;
            z-index: 9999;
            background: ${bg};
            color: white;
            padding: 14px 16px;
            text-align: center;
            font-family: system-ui, -apple-system, sans-serif;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            animation: ctBannerSlideDown 0.3s ease-out;
        `;

        // Inject keyframes if not already present
        if (!document.querySelector('#campaign-token-banner-styles')) {
            const style = document.createElement('style');
            style.id = 'campaign-token-banner-styles';
            style.textContent = `
                @keyframes ctBannerSlideDown {
                    from { transform: translateY(-100%); }
                    to   { transform: translateY(0); }
                }
                @keyframes ctBannerSlideUp {
                    from { transform: translateY(0); }
                    to   { transform: translateY(-100%); }
                }
            `;
            document.head.appendChild(style);
        }

        el.innerHTML = `
            <div style="display:flex; justify-content:center; align-items:center; gap:10px; max-width:1200px; margin:0 auto;">
                <span style="font-size:1.3em;">${icon}</span>
                <div>
                    ${banner.title ? `<strong>${banner.title}</strong>` : ''}
                    ${banner.title && banner.message ? ' — ' : ''}
                    ${banner.message || ''}
                </div>
                <button onclick="this.closest('#campaign-token-banner').style.animation='ctBannerSlideUp 0.3s ease-out forwards';setTimeout(()=>this.closest('#campaign-token-banner').remove(),300)"
                        style="background:rgba(255,255,255,0.2); border:none; color:white; padding:4px 10px; border-radius:4px; cursor:pointer; margin-left:12px; font-size:1.1em;">
                    ✕
                </button>
            </div>
        `;

        // Remove any existing banner
        document.getElementById('campaign-token-banner')?.remove();
        document.body.appendChild(el);

        // Auto-dismiss after 15 seconds
        setTimeout(() => {
            if (el.parentElement) {
                el.style.animation = 'ctBannerSlideUp 0.3s ease-out forwards';
                setTimeout(() => el.remove(), 300);
            }
        }, 15000);
    }

    /** Returns the validated token data, or null if no token / invalid */
    public getTokenData(): CampaignTokenData | null {
        return this.tokenData;
    }

    /** Returns raw campaign pricing, or null if no pricing in this campaign */
    public getPricing(): CampaignPricing | null {
        return this.tokenData?.pricing ?? null;
    }

    /** Returns resolved display-ready pricing values, or null for default rates */
    public getResolvedPricing() {
        return resolvePricing(this.tokenData?.pricing);
    }

    /** Returns the lead_id from the URL, if present */
    public getLeadId(): string | null {
        return this.leadId;
    }

    /** Redeem the token (call after booking is completed) */
    public async redeemToken(): Promise<boolean> {
        if (!this.token) return false;
        try {
            const response = await fetch(
                `${CAMPAIGN_TOKEN_BASE}/${encodeURIComponent(this.token)}/redeem`,
                { method: 'POST' }
            );
            if (response.ok) {
                analytics.track('campaign_token_redeemed', 'campaign_token_service', {
                    token: this.token,
                    lead_id: this.leadId,
                });
                return true;
            }
            return false;
        } catch (error) {
            console.error('Failed to redeem campaign token:', error);
            return false;
        }
    }
}

export const campaignTokenService = CampaignTokenService.getInstance();
export default campaignTokenService;
